import { RFQChecklistCard, USER_ACTIONS } from 'types/enrollmentService';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { SupportedUserRoles } from '@insurancemenu/imenu-components-lib/dist/types';
import { celticsRights, uwRights, brokerRights } from '@insurancemenu/imenu-components-lib/dist/utils/accessRights';
import { RateSegment } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';

interface RFQChecklistURLData {
  periodId: string;
  cartId: string;
}

export interface RFQCardTextObject {
  title: string;
  text: string;
  redirectTo: (cartId: string, periodId: string, groupId?: string, defaultProductLineId?: ProductLineId) => string;
  isAvailable?: (userRoles: SupportedUserRoles[]) => boolean;
}

export interface UserActionsByChecklistCard {
  card: RFQChecklistCard;
  userActions: USER_ACTIONS;
}

type CardDataObjectType = {
  [K in RFQChecklistCard]: RFQCardTextObject;
};

export const RFQ_PROPOSAL_CARD_DATA: CardDataObjectType = Object.freeze({
  // Initiate checklist
  INITIATE_RFQ: {
    title: 'Initiate Custom/Underwritten Quote',
    text: 'Enter the details for this proposal',
    redirectTo: (cartId: string, periodId: string): string => {
      return periodId ? `/initiate-rfq/${periodId}?cartId=${cartId}` : `/initiate-rfq?cartId=${cartId}`;
    },
  },
  INITIATE_SHELF: {
    title: 'Initiate Shelf Plan Quote',
    text: 'Enter the details for this proposal',
    redirectTo: (cartId: string, periodId: string): string => {
      return periodId ? `/initiate-shelf/${periodId}?cartId=${cartId}` : `/initiate-shelf?cartId=${cartId}`;
    },
  },
  INITIATE_LEVEL_FUNDED: {
    title: 'Initiate Level Funded Quote',
    text: 'Enter the details for this proposal',
    redirectTo: (cartId: string, periodId: string): string => {
      return periodId
        ? `/initiate-level-funded/${periodId}?cartId=${cartId}`
        : `/initiate-level-funded?cartId=${cartId}`;
    },
  },
  INITIATE_ADD_EMPLOYEES: {
    title: 'Add Employees',
    text: 'Add employees and monitor their progress',
    redirectTo: (cartId: string, periodId: string, groupId = ''): string => {
      return `/initiate-add-employees/${periodId}?cartId=${cartId}&groupId=${groupId}`;
    },
    tutorialLink: '#',
  },
  INITIATE_RFQ_DOCUMENTS: {
    title: 'Upload Documents',
    text: 'Upload/retrieve supporting documents for RFQ',
    redirectTo: (cartId: string, periodId: string, groupId = ''): string => {
      return `/initiate-rfq-documents/${periodId}?cartId=${cartId}&groupId=${groupId}`;
    },
  },
  SUBMIT_INITIATE_RFQ: {
    title: 'Submit RFQ',
    text: 'Add the recipients you want to share the proposal with',
    redirectTo: (cartId: string, periodId: string): string => {
      return `/submit-initiate-rfq/${periodId}?cartId=${cartId}`;
    },
  },
  PICK_CARRIERS: {
    title: 'Pick Carriers',
    text: "Select carriers to request proposals for the product lines you're interested in",
    redirectTo: (cartId: string, periodId: string): string => {
      return `/pick-carriers/${periodId}?cartId=${cartId}`;
    },
  },
  INITIATE_SHELF_BROWSING: {
    title: 'Browse and Build Shelf Proposal',
    text: 'Select plan configurations to add to a proposal and generate customer outputs',
    redirectTo: (cartId: string, periodId: string, groupId = '', defaultProductLineId = 'GroupMedical'): string => {
      return `/browsing?periodId=${periodId}&cartId=${cartId}&groupId=${groupId}&productLineId=${defaultProductLineId}&type=rfq`;
    },
  },
  INITIATE_LEVEL_BROWSING: {
    title: 'Browse and Build Level Funded Proposal',
    text: 'Select illustrative level funded plans to add to a proposal prior to gathering carrier required documents',
    redirectTo: (cartId: string, periodId: string, groupId = '', defaultProductLineId = 'LevelMedical'): string => {
      return `/browsing?periodId=${periodId}&cartId=${cartId}&groupId=${groupId}&productLineId=${defaultProductLineId}&type=rfq`;
    },
  },
  INITIATE_BROWSING_CARRIERS: {
    title: 'Trigger Carrier Response Checklist',
    text: 'Based on the carriers and level funded products selected, InsuranceMenu will create checklists to track collection of documents and carrier responses',
    redirectTo: (cartId: string, periodId: string): string => {
      return `/browsing-review-carriers/${periodId}?cartId=${cartId}`;
    },
  },

  // Response checklist
  BROKER_MESSAGE: {
    title: 'Broker Message',
    text: "Review the broker's message and confirm your acknowledgment of the provided information",
    redirectTo: (cartId: string, periodId: string): string => {
      return `/broker-message/${periodId}?cartId=${cartId}`;
    },
  },
  MONITOR_ADD_EMPLOYEES: {
    title: 'Add Employees',
    text: 'Add employees and monitor their progress',
    redirectTo: (cartId: string, periodId: string, groupId = ''): string => {
      return `/monitor-add-employees/${periodId}?cartId=${cartId}&groupId=${groupId}`;
    },
    tutorialLink: '#',
  },
  RFQ_RESPONSE_DOCUMENTS: {
    title: 'Upload Documents',
    text: 'Upload/retrieve supporting documents for RFQ',
    redirectTo: (cartId: string, periodId: string, groupId = ''): string => {
      return `/rfq-response-documents/${periodId}?cartId=${cartId}&groupId=${groupId}`;
    },
  },
  DOWNLOAD_IMQS: {
    title: 'Download IMQs',
    text: 'Download all employee-related documents',
    redirectTo: (cartId: string, periodId: string, groupId = ''): string => {
      return `/download-imqs/${periodId}?cartId=${cartId}&groupId=${groupId}`;
    },
  },
  RFQ_DETAILS: {
    title: 'RFQ Details',
    text: 'Provide additional RFQ details if needed',
    redirectTo: (cartId: string, periodId: string): string => {
      return `/rfq-details/${periodId}?cartId=${cartId}`;
    },
  },
  BUILD_RFQ: {
    title: 'Build RFQ Proposal',
    text: 'Pick or configure product templates and product lines',
    redirectTo: (cartId: string, periodId: string, groupId = '', defaultProductLineId = 'CustomDental'): string => {
      return `/browsing?periodId=${periodId}&cartId=${cartId}&groupId=${groupId}&productLineId=${defaultProductLineId}&type=rfq`;
    },
    isAvailable: (userRoles: SupportedUserRoles[]): boolean => {
      return celticsRights(userRoles);
    },
  },
  CREATE_PORTFOLIO: {
    title: 'View RFQ Cart',
    text: 'Review product selection and create multi-product portfolios as needed',
    redirectTo: (cartId: string, periodId: string, groupId = ''): string => {
      return `/rfq-proposal/${periodId}?cartId=${cartId}&groupId=${groupId}&type=create-portfolio`;
    },
    isAvailable: (userRoles: SupportedUserRoles[]): boolean => {
      return uwRights(userRoles);
    },
  },
  RATE_RFQ: {
    title: 'Underwrite/Rate RFQ Proposal',
    text: 'Enter rates and underwriting assumptions for response',
    redirectTo: (cartId: string, periodId: string, groupId = ''): string => {
      return `/rfq-proposal/${periodId}?cartId=${cartId}&groupId=${groupId}&type=rate`;
    },
    isAvailable: (userRoles: SupportedUserRoles[]): boolean => {
      return uwRights(userRoles);
    },
  },
  ADD_COLLATERAL: {
    title: 'Add Collateral',
    text: 'Upload additional collateral',
    redirectTo: (cartId: string, periodId: string, groupId = ''): string => {
      return `/rfq-proposal/${periodId}?cartId=${cartId}&groupId=${groupId}&type=collateral`;
    },
    isAvailable: (userRoles: SupportedUserRoles[]): boolean => {
      return uwRights(userRoles);
    },
  },
  RELEASE: {
    title: 'Release RFQ for Review',
    text: 'Release client ready response',
    redirectTo: (cartId: string, periodId: string, groupId = ''): string => {
      return `/rfq-proposal/${periodId}?cartId=${cartId}&groupId=${groupId}&type=release`;
    },
    isAvailable: (userRoles: SupportedUserRoles[]): boolean => {
      return celticsRights(userRoles);
    },
  },
  PROPOSAL_RESPONSE: {
    title: 'Response Proposal',
    text: 'Upload the response proposal along with any additional information needed to complete the final step',
    redirectTo: (cartId: string, periodId: string): string => {
      return `/response-proposal/${periodId}?cartId=${cartId}`;
    },
  },

  // Level Funded Response checklist
  RATE_LEVEL_FUNDED: {
    title: 'Rate Level Funded Plans',
    text: 'Enter final rates for the Level Funded plans',
    redirectTo: (cartId: string, periodId: string, groupId = ''): string => {
      return `/rfq-proposal/${periodId}?cartId=${cartId}&groupId=${groupId}&type=rate`;
    },
    isAvailable: (userRoles: SupportedUserRoles[]): boolean => {
      return brokerRights(userRoles);
    },
  },

  /** @deprecated: Create a new "Upload Documents" page for specific cases. */
  UPLOAD_DOCUMENTS: {
    title: 'Upload Documents',
    text: 'Upload/retrieve supporting documents for RFQ',
    redirectTo: (cartId: string, periodId: string): string => {
      return `/upload-rfq-documents/${periodId}?cartId=${cartId}`;
    },
  },
  /** @deprecated: Create a new "Upload Documents" page for specific cases. */
  INITIATE_BROWSING: {
    title: 'Build Proposal',
    text: 'Pick products and product lines',
    redirectTo: (cartId: string, periodId: string, groupId = '', defaultProductLineId = 'LevelMedical'): string => {
      return `/browsing?periodId=${periodId}&cartId=${cartId}&groupId=${groupId}&productLineId=${defaultProductLineId}&type=rfq`;
    },
  },
});

/**
 * Generates a URL for the RFQ checklist based on the provided params.
 * @returns The constructed URL for the RFQ checklist.
 */
export const getRFQProposalChecklistURL = ({ periodId, cartId }: RFQChecklistURLData): string => {
  return `/rfq-checklist/${periodId}?cartId=${cartId}`;
};

export const getBrowsingUserActionByRateSegment = (rateSegment: RateSegment): UserActionsByChecklistCard => {
  switch (rateSegment) {
    case 'CUSTOM': {
      return {
        card: RFQChecklistCard.RATE_RFQ,
        userActions: USER_ACTIONS.COMPLETE_BUILD_RFQ,
      };
    }
    case 'GROUP':
    case 'LEVEL': {
      return {
        card: RFQChecklistCard.INITIATE_SHELF_BROWSING,
        userActions: USER_ACTIONS.COMPLETE_INITIATE_BROWSING,
      };
    }
    default:
      return {
        card: RFQChecklistCard.RATE_RFQ,
        userActions: USER_ACTIONS.COMPLETE_BUILD_RFQ,
      };
  }
};

const stepsWithFiles = [RFQChecklistCard.DOWNLOAD_IMQS];

export const isFilesStep = (card: RFQChecklistCard): boolean => {
  return stepsWithFiles.includes(card);
};
